import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { createSalesInvoiceTool } from '../gemini-function/sales-invoice-function';
import { getHistoricalDataTool, getCurrentPeriodDataTool } from '../gemini-function/profit-data-functions';
import { getFirestore, doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

@Injectable({ providedIn: 'root' })
export class AgentProfitService {
  private functions = inject(Functions, { optional: true });
  
  // Keep track of conversation history so Gemini remembers context
  private chatHistory: any[] = [];
  private pastGeminiHistory: any[] = [];
  private cancelSubject = new Subject<void>();

  // Programmatic Chat UI Visibility & Context Controller
  private chatOpenSubject = new BehaviorSubject<{ open: boolean; agentId?: string; deviceId?: string }>({ open: false });
  chatOpen$ = this.chatOpenSubject.asObservable();

  openChat(options?: { agentId?: string; deviceId?: string }) {
    this.chatOpenSubject.next({ open: true, agentId: options?.agentId, deviceId: options?.deviceId });
  }

  openChatForDevice(deviceId: string) {
    this.openChat({ agentId: 'kiosk-support', deviceId: deviceId });
  }

  closeChat() {
    this.chatOpenSubject.next({ open: false });
  }

  toggleChat() {
    const current = this.chatOpenSubject.value;
    this.chatOpenSubject.next({ open: !current.open });
  }

  async loadUserPreferences(userId?: string): Promise<{ agentId?: string; deviceId?: string } | null> {
    try {
      const auth = getAuth();
      const uid = userId || auth.currentUser?.uid;
      if (!uid) return null;
      const db = getFirestore();
      const docRef = doc(db, 'users', uid, 'preferences', 'agent_chat');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data() as { agentId?: string; deviceId?: string };
      }
    } catch (e) {
      console.warn('Could not load user chat preferences from Firestore.', e);
    }
    return null;
  }

  async saveUserPreferences(prefs: { agentId?: string; deviceId?: string }, userId?: string) {
    try {
      const auth = getAuth();
      const uid = userId || auth.currentUser?.uid;
      if (!uid) return;
      const db = getFirestore();
      const docRef = doc(db, 'users', uid, 'preferences', 'agent_chat');
      await setDoc(docRef, {
        agentId: prefs.agentId || null,
        deviceId: prefs.deviceId || null,
        updatedAt: Date.now()
      }, { merge: true });
    } catch (e) {
      console.warn('Could not save user chat preferences to Firestore.', e);
    }
  }

  constructor(private http: HttpClient, private configService: ConfigService) {}

  async loadChatHistory(userId?: string): Promise<any[]> {
    try {
      const auth = getAuth();
      const uid = userId || auth.currentUser?.uid || 'default_user';
      const db = getFirestore();
      const docRef = doc(db, 'chat_history', uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const now = Date.now();
        const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
        const today = new Date().setHours(0, 0, 0, 0);

        if (data['geminiHistory']) {
          const validHistory = data['geminiHistory'].filter((msg: any) => 
            msg.timestamp && msg.timestamp >= sevenDaysAgo
          );

          this.chatHistory = validHistory.filter((msg: any) => 
            msg.timestamp && new Date(msg.timestamp).setHours(0, 0, 0, 0) === today
          );

          this.pastGeminiHistory = validHistory.filter((msg: any) => 
            msg.timestamp && new Date(msg.timestamp).setHours(0, 0, 0, 0) !== today
          );
        }
        
        let uiMessages = data['uiMessages'] || [];
        return uiMessages.filter((msg: any) => !msg.timestamp || msg.timestamp >= sevenDaysAgo);
      }
    } catch (e) {
      console.warn('Could not load chat history from Firebase.', e);
    }
    return [];
  }

  async saveChatHistory(uiMessages: any[], userId?: string) {
    try {
      const auth = getAuth();
      const uid = userId || auth.currentUser?.uid || 'default_user';
      const db = getFirestore();
      const docRef = doc(db, 'chat_history', uid);
      
      // Omit non-serializable properties like SafeHtml
      const serializedMessages = uiMessages.map(msg => ({
        sender: msg.sender,
        text: msg.text,
        rawHtml: msg.rawHtml || '',
        timestamp: msg.timestamp || Date.now()
      }));

      await setDoc(docRef, {
        uiMessages: serializedMessages,
        geminiHistory: [...this.pastGeminiHistory, ...this.chatHistory]
      }, { merge: true });
    } catch (e) {
      console.warn('Could not save chat history to Firebase.', e);
    }
  }

  async getAgentConfig(agentId: string): Promise<{ metaprompt?: string, name?: string, isActive?: boolean }> {
    try {
      const db = getFirestore();
      const docRef = doc(db, 'agents', agentId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return { 
          metaprompt: data['metaprompt'], 
          name: data['name'],
          isActive: data['isActive'] !== false
        };
      }
    } catch (e) {
      console.warn('Could not load agent config from Firebase for agentId:', agentId, e);
    }
    return {};
  }

  async getActiveAgents(): Promise<{ agentId: string, name: string, description?: string, icon?: string, badge?: string, starterPrompts?: string[], metaprompt: string }[]> {
    try {
      const db = getFirestore();
      const agentsCol = collection(db, 'agents');
      const snap = await getDocs(agentsCol);
      const list: { agentId: string, name: string, description?: string, icon?: string, badge?: string, starterPrompts?: string[], metaprompt: string }[] = [];
      snap.forEach(d => {
        const data = d.data();
        if (data['isActive'] !== false) {
          list.push({
            agentId: d.id,
            name: data['name'] || d.id,
            description: data['description'] || '',
            icon: data['icon'] || undefined,
            badge: data['badge'] || undefined,
            starterPrompts: data['starterPrompts'] || [],
            metaprompt: data['metaprompt'] || ''
          });
        }
      });
      return list.sort((a, b) => a.name.localeCompare(b.name));
    } catch (e) {
      console.warn('Could not fetch active agents:', e);
      return [];
    }
  }

  private cachedBlueprintMetadata: any = null;

  async getBlueprintCache(): Promise<string | null> {
    if (this.cachedBlueprintMetadata?.name) {
      return this.cachedBlueprintMetadata.name;
    }
    try {
      const data = await firstValueFrom(this.http.get<any>('assets/blueprint-cache.json'));
      if (data && data.name) {
        this.cachedBlueprintMetadata = data;
        return data.name;
      }
    } catch (e) {
      // Ignored if assets/blueprint-cache.json is not present
    }
    return null;
  }

  cancelAgentRequest() {
    this.cancelSubject.next();
  }

  async askAgent(query: string, context: any = {}, systemInstruction?: string, agentId: string = 'financial-operations'): Promise<any> {
    try {
      // 1. Primary path: Invoke backend MCP Agent Orchestrator (executeAgentTurn)
      if (this.functions) {
        try {
          const executeFn = httpsCallable<any, any>(this.functions, 'executeAgentTurn');
          const res = await executeFn({
            agentId: agentId,
            message: query,
            chatHistory: this.chatHistory,
            context: context
          });

          if (res?.data?.text) {
            this.chatHistory.push({ role: 'user', text: query, parts: [{ text: query }], timestamp: Date.now() });
            this.chatHistory.push({ role: 'model', text: res.data.text, parts: [{ text: res.data.text }], timestamp: Date.now() });
            return {
              text: res.data.text,
              action: res.data.actionProposals?.[0]?.action,
              data: res.data.actionProposals?.[0]
            };
          }
        } catch (cloudFnErr: any) {
          console.warn('Backend Cloud Function executeAgentTurn unavailable or pending deployment, using direct client engine with Context Cache:', cloudFnErr?.message || cloudFnErr);
        }
      }

      // 2. Direct client fallback (with Gemini Context Caching)...
      const defaultInstruction = `You are an expert ERP AI assistant for KCEG photobooth operations. 
      Answer the user's questions strictly using factual data and provided tools.
      
      CRITICAL DATA RELATIONSHIPS:
      - 'totalOpsAndFees' (in the dashboard data) represents the sum of ALL deductions: Operating Expenses + SMRS Fees + Sales Tax + Adjustments - Invoice Total Due (Operation Expense).
      - Operating expenses, sales tax, invoices, and adjustments are available via the 'getCurrentPeriodData' and 'getHistoricalData' tools.
      - PROFIT FIELDS DIFFERENTIATION: The data contains 'NetProfit' (Standard), 'ShopNetProfit' (Shop-in-Shop), and 'BrokerNetProfit' (Broker). Use the specific profit type that is populated for each company. When summarizing, distinguish between these types and do NOT mix them up.
      - COMPANIES: The data contains an 'IsKCEGOwned' column indicating if KCEG owns the company ('Yes' or 'No').
      - FEE COLLECTION: The data contains an 'FCBreakdown' column for Fee Collection companies. Their primary income is collected fees from other companies, detailed in 'FCBreakdown' (e.g., source company, status, amount). If 'FCBreakdown' is present, the company is a Fee Collection company.
      - If expenses are empty or 0, it means NO operating expenses have been uploaded for that period. Do NOT hallucinate expenses. Explain this clearly to the user.

      When identifying or calculating fixed costs, use the Chart of Accounts to strictly isolate and maintain them based on these rules:
      1. Payroll & Labor: Look for "Salaries", "Contractor Fees" (for core engineers), and "Payroll Taxes". Since the team is steady, this rarely fluctuates wildly.
      2. Technology & Infrastructure: Look for "Cloud Hosting", "SaaS Subscriptions", and "API Fees".
      3. Professional Services: Accounting fees, legal retainers, or recurring compliance costs.
      4. Occupancy/Admin: Business insurance, registered agent fees, and basic office overhead.

      If the user asks for a projection, analyze the trends in revenue and variable expenses over the 6 months, project them forward, and carry over the identified fixed costs relatively steady based on the rules above.
      If asked about Net Income trends or fixed costs per company, use the 'getHistoricalData' tool. If asked about current period data, use the 'getCurrentPeriodData' tool.
      If you cannot find the answer in the data, politely say you need more data.
      Format your numbers cleanly as currency when appropriate.`;
      
      const toolInstruction = `\nNOTE: You MUST call the 'getCurrentPeriodData' tool to fetch company data for the current period, OR call 'getHistoricalData' if you need the past 6 months. Do not assume you have company data until you call a tool.`;
      const finalInstruction = (systemInstruction || defaultInstruction) + toolInstruction;

      // When screen context is provided, attach it seamlessly
      let userQueryText = query;
      if (context?.period || context?.stats || context?.deviceId) {
        const notes: string[] = [];
        if (context.period) notes.push(`[Screen Period: ${context.period}]`);
        if (context.deviceId) notes.push(`[Target Photobooth Device: ${context.deviceId}]`);
        if (context.stats) notes.push(`[Screen Dashboard Stats: ${JSON.stringify(context.stats)}]`);
        if (context.chartOfAccounts?.length) notes.push(`[Chart of Accounts: ${context.chartOfAccounts.join(', ')}]`);
        userQueryText = `${notes.join(' ')}\n\n${query}`;
      }

      // Add the user's current question to the history
      this.chatHistory.push({ role: 'user', parts: [{ text: userQueryText }], timestamp: Date.now() });

      const apiKey = this.configService.getConfig().geminiApiKey;

      if (!apiKey) {
        throw new Error('Gemini API key is not configured in the environment settings. Please check your Cloud Function and Secret Manager.');
      }

      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`;

      const cachedContentName = await this.getBlueprintCache();
      let payload: any;

      if (cachedContentName) {
        // High-efficiency mode: Blueprint knowledge (~52,000 tokens), system instructions, and tools are pre-cached on Google Gemini
        payload = {
          cachedContent: cachedContentName,
          contents: this.chatHistory.map(msg => ({
            role: msg.role,
            parts: msg.parts
          })),
          generationConfig: { temperature: 0.1 }
        };
      } else {
        // Fallback inline mode
        const periodText = context?.period ? `\n--- CURRENT PERIOD: ${context.period} ---` : '';
        const statsText = context?.stats ? `\nDASHBOARD STATS: ${JSON.stringify(context.stats)}` : '';
        const coaText = context?.chartOfAccounts ? `\nCHART OF ACCOUNTS: ${context.chartOfAccounts.join(', ')}` : '';
        const systemPrompt = `${finalInstruction}${periodText}${statsText}${coaText}`;

        payload = {
          systemInstruction: { parts: [{ text: systemPrompt }] },
          contents: this.chatHistory.map(msg => ({
            role: msg.role,
            parts: msg.parts
          })),
          generationConfig: { temperature: 0.1 },
          tools: [createSalesInvoiceTool, getHistoricalDataTool, getCurrentPeriodDataTool]
        };
      }

      let response: any;
      try {
        response = await firstValueFrom(this.http.post<any>(apiUrl, payload).pipe(takeUntil(this.cancelSubject)));
      } catch (postErr: any) {
        if (cachedContentName) {
          console.warn('Gemini cached content call failed or expired. Falling back to inline prompt:', postErr);
          this.cachedBlueprintMetadata = null;
          const periodText = context?.period ? `\n--- CURRENT PERIOD: ${context.period} ---` : '';
          const statsText = context?.stats ? `\nDASHBOARD STATS: ${JSON.stringify(context.stats)}` : '';
          const coaText = context?.chartOfAccounts ? `\nCHART OF ACCOUNTS: ${context.chartOfAccounts.join(', ')}` : '';
          const systemPrompt = `${finalInstruction}${periodText}${statsText}${coaText}`;

          payload = {
            systemInstruction: { parts: [{ text: systemPrompt }] },
            contents: this.chatHistory.map(msg => ({
              role: msg.role,
              parts: msg.parts
            })),
            generationConfig: { temperature: 0.1 },
            tools: [createSalesInvoiceTool, getHistoricalDataTool, getCurrentPeriodDataTool]
          };
          response = await firstValueFrom(this.http.post<any>(apiUrl, payload).pipe(takeUntil(this.cancelSubject)));
        } else {
          throw postErr;
        }
      }
      let turns = 0;

      // Execute up to 3 tool-call cycles automatically
      while (response.candidates && response.candidates.length > 0 && turns < 3) {
        turns++;
        const candidate = response.candidates[0];
        
        if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
          console.warn('Empty content from Gemini:', candidate);
          return { text: `Sorry, I couldn't generate a response. (Reason: ${candidate.finishReason || 'Unknown'})` };
        }

        const part = candidate.content.parts[0];

        if (part.functionCall) {
          const call = part.functionCall;
          
          if (call.name === 'getHistoricalData') {
            // 1. Add model's function request to history
            this.chatHistory.push({ role: 'model', parts: [{ functionCall: call }], timestamp: Date.now() });
            
            // 2. Resolve target company and construct CSV payload locally
            const targetCompany = call.args?.companyName?.toLowerCase();
            let compactHistorical = '';
            if (Array.isArray(context.historicalData)) {
              context.historicalData.forEach((h: any) => {
                let periodData = `\n--- Period: ${h.period} ---\nCompany,Brand,Type,IsKCEGOwned,Rev,Fees,Exp,NetProfit,ShopNetProfit,BrokerNetProfit,Tax,Due,Adj,ExpensesByCategory,Partners,StoreDetails,FCBreakdown\n`;
                let hasData = false;
                if (Array.isArray(h.companies)) {
                  h.companies.forEach((c: any) => {
                    if (!c.totalRevenue && !c.totalFees && !c.totalExpenses && !c.netProfit) return;
                    if (targetCompany && !(c.companyName || '').toLowerCase().includes(targetCompany)) return;
                    
                    let expCat = '';
                    if (c.expensesByCategory) {
                      expCat = Object.entries(c.expensesByCategory).filter(([_, v]) => v).map(([k, v]) => `${k}:${v}`).join(';');
                    }
                    const np = c.reportType === 'standard' ? (c.netProfit || 0) : '';
                    const snp = c.reportType === 'sis' ? (c.shopNetProfit || 0) : '';
                    const bnp = c.reportType === 'broker' ? (c.brokerNetProfit || 0) : '';
                    periodData += `"${c.companyName}","${c.brand || ''}",${c.reportType || 'standard'},"${c.isKcegOwned ? 'Yes' : 'No'}",${c.totalRevenue || 0},${c.totalFees || 0},${c.totalExpenses || 0},${np},${snp},${bnp},${c.salesTaxAmount || 0},${c.invoiceTotalDue || 0},${c.totalAdjustments || 0},${expCat},"${c.partners || ''}","${c.storeDetails || ''}","${c.feeCollectionBreakdown || ''}"\n`;
                    hasData = true;
                  });
                }
                if (hasData) compactHistorical += periodData;
              });
            }
            if (!compactHistorical) compactHistorical = "No historical data found.";
            
            // 3. Push tool response back to history as the user
            this.chatHistory.push({ 
              role: 'user', 
              parts: [{ functionResponse: { name: call.name, response: { content: compactHistorical } } }], 
              timestamp: Date.now() 
            });
            
            // 4. Update the payload and seamlessly request the model's text response again
            payload.contents = this.chatHistory.map(msg => ({
              role: msg.role,
              parts: msg.parts
            }));
            
            response = await firstValueFrom(this.http.post<any>(apiUrl, payload).pipe(takeUntil(this.cancelSubject)));
            continue; // Cycle loops to handle the returned candidate
          } else if (call.name === 'getCurrentPeriodData') {
            this.chatHistory.push({ role: 'model', parts: [{ functionCall: call }], timestamp: Date.now() });

            const targetCompany = call.args?.companyName?.toLowerCase();
            let compactCurrent = '';
            if (Array.isArray(context.historicalData)) {
              const currentPeriodData = context.historicalData.find((h: any) => h.period === context.period);
              if (currentPeriodData) {
                let periodData = `\n--- Current Period: ${currentPeriodData.period} ---\nCompany,Brand,Type,IsKCEGOwned,Rev,Fees,Exp,NetProfit,ShopNetProfit,BrokerNetProfit,Tax,Due,Adj,ExpensesByCategory,Partners,StoreDetails,FCBreakdown\n`;
                let hasData = false;
                if (Array.isArray(currentPeriodData.companies)) {
                  currentPeriodData.companies.forEach((c: any) => {
                    if (!c.totalRevenue && !c.totalFees && !c.totalExpenses && !c.netProfit) return;
                    if (targetCompany && !(c.companyName || '').toLowerCase().includes(targetCompany)) return;
                    
                    let expCat = '';
                    if (c.expensesByCategory) {
                      expCat = Object.entries(c.expensesByCategory).filter(([_, v]) => v).map(([k, v]) => `${k}:${v}`).join(';');
                    }
                    const np = c.reportType === 'standard' ? (c.netProfit || 0) : '';
                    const snp = c.reportType === 'sis' ? (c.shopNetProfit || 0) : '';
                    const bnp = c.reportType === 'broker' ? (c.brokerNetProfit || 0) : '';
                    periodData += `"${c.companyName}","${c.brand || ''}",${c.reportType || 'standard'},"${c.isKcegOwned ? 'Yes' : 'No'}",${c.totalRevenue || 0},${c.totalFees || 0},${c.totalExpenses || 0},${np},${snp},${bnp},${c.salesTaxAmount || 0},${c.invoiceTotalDue || 0},${c.totalAdjustments || 0},${expCat},"${c.partners || ''}","${c.storeDetails || ''}","${c.feeCollectionBreakdown || ''}"\n`;
                    hasData = true;
                  });
                }
                if (hasData) compactCurrent += periodData;
              }
            }
            if (!compactCurrent) compactCurrent = "No current period data found.";
            
            this.chatHistory.push({ 
              role: 'user', 
              parts: [{ functionResponse: { name: call.name, response: { content: compactCurrent } } }], 
              timestamp: Date.now() 
            });
            
            payload.contents = this.chatHistory.map(msg => ({
              role: msg.role,
              parts: msg.parts
            }));
            
            response = await firstValueFrom(this.http.post<any>(apiUrl, payload).pipe(takeUntil(this.cancelSubject)));
            continue;
          } else if (call.name === 'createSalesInvoice') {
            const agentText = `I have successfully created the invoice for ${call.args.companyName || 'the company'}.`;

            this.chatHistory.push({ role: 'model', parts: [{ text: agentText }], timestamp: Date.now() });
            return { action: call.name, data: call.args, text: agentText };
          }
        }
        
        if (part.text) {
          const agentText = part.text;
          this.chatHistory.push({ role: 'model', parts: [{ text: agentText }], timestamp: Date.now() });
          return { text: agentText };
        }
      }

      return { text: "Sorry, I couldn't generate a response." };

    } catch (error: any) {
      console.error('Agent Service Error', error);
      // If the request fails, remove the last user message so they can try again
      this.chatHistory.pop();
      
      if (error && error.name === 'EmptyError') {
        return { text: "Thinking stopped by user.", stopped: true };
      }
      
      // Provide a much better error message to the UI
      let errorMsg = 'Sorry, I encountered an error connecting to the AI.';
      if (error.status === 400) errorMsg = 'API Error 400: Bad Request. The API key might be invalid.';
      if (error.status === 403) errorMsg = 'API Error 403: Forbidden. Check your Gemini API Key.';
      if (error.error && error.error.error && error.error.error.message) {
        errorMsg += ` Details: ${error.error.error.message}`;
      }
      
      throw new Error(errorMsg);
    }
  }

  // Optional: Call this from your Component if you want a "Clear Chat" button
  clearHistory() {
    this.chatHistory = [];
    this.pastGeminiHistory = [];
  }
}
